import {
  AUTOMATION_PLATFORMS,
  DAY_NAMES,
  type AutomationPlatform,
  type DayOfWeek,
} from '@backend/lib/db/schema/automation'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
} from '@/components/ui/icons/icons-new'
import type { RouterOutput } from '@/config/trpc'

/** An automation as returned by the tRPC client (dates arrive as ISO strings). */
export type AutomationItem = RouterOutput['automation']['list'][number]

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}

export const DAY_SHORT: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
}

export const PLATFORM_LABELS: Record<AutomationPlatform, string> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
}

export const platformIcons: Record<AutomationPlatform, typeof InstagramIcon> = {
  linkedin: LinkedinIcon,
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
  facebook: FacebookIcon,
}

/** Brand colours for the colored-circle platform icons — mirrors the workspace/posting UI. */
export const PLATFORM_STYLES: Record<AutomationPlatform, string> = {
  linkedin: 'bg-blue-500',
  instagram: 'bg-pink-500',
  tiktok: 'bg-black ring-1 ring-white/20',
  facebook: 'bg-[#1877F2]',
}

/**
 * Toggle a platform in a selection, refusing to leave it empty (the schema
 * enforces a min of 1). Returns the selection in canonical platform order.
 */
export function togglePlatform(
  current: AutomationPlatform[],
  platform: AutomationPlatform,
): AutomationPlatform[] {
  if (current.includes(platform)) {
    const next = current.filter((p) => p !== platform)
    return next.length > 0 ? next : current
  }
  return AUTOMATION_PLATFORMS.filter((p) => current.includes(p) || p === platform)
}

/** Today's day-of-week in the app's Monday-first vocabulary. */
export function getToday(): DayOfWeek {
  // JS getDay(): 0 = Sunday … 6 = Saturday → shift to a Monday-first index.
  return DAY_NAMES[(new Date().getDay() + 6) % 7] ?? 'monday'
}
